import { useEffect, useState } from 'react';
import { subscribeMembers, type MemberListResult } from '@/lib/firestoreMembers';

/**
 * Hook que envolve `subscribeMembers` em estado React. `LOADING` no primeiro
 * tick, `READY`/`ERROR` em seguida. Limpa o listener no unmount e quando
 * `tenantId` muda. *
 * Origem: sem issue — commit f948c64e4, 2026-06-10 18:39 +0200 (abas Usuários e
 * Responsáveis na página do tenant).
 *
 * Usado por: `features/members/MembersTab.tsx` (aba Usuários) e
 * `features/printers/PrintersTab.tsx` (nome de quem cadastrou a impressora).
 *
 * Dados: `tenants/{tenantId}/members` (listener, sem ordenação nem limite),
 * validado por `MemberSchema` de `@etiquetachef/types` em
 * `lib/firestoreMembers.ts`. `tenantId` vazio fica em `LOADING`.
 *
 * Falhas conhecidas: documento fora do schema é descartado no `lib/` só com
 * `console.warn`, e erro do listener só com `console.error` — nenhum dos dois
 * chega à telemetria (CLAUDE.md "Observabilidade"; ver #926, achados).
 */
export function useMembers(tenantId: string): MemberListResult {
  const [result, setResult] = useState<MemberListResult>({ kind: 'LOADING' });

  useEffect(() => {
    if (!tenantId) {
      setResult({ kind: 'LOADING' });
      return;
    }
    setResult({ kind: 'LOADING' });
    const unsub = subscribeMembers(tenantId, setResult);
    return unsub;
  }, [tenantId]);

  return result;
}
